/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React, { useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ListItemIcon, ListItemText, makeStyles, Menu, MenuItem, Tooltip } from '@material-ui/core';
import AutorenewIcon from '@material-ui/icons/Autorenew';
import DeleteIcon from '@material-ui/icons/Delete';
import ImportExport from '@material-ui/icons/ImportExport';
import { KeyboardArrowDown } from '@material-ui/icons';
import { DraftManagementContext } from '../useDraftManagement';
import TableFilter from '../../../components/foTable/foTableFilter/TableFilter';
import Button from '../../../components/common/Button';
import { useScreenSize } from '../../../shared/theming/useScreenSize';

const useStyles = makeStyles(theme => ({
  ...theme.customTheme,
  tooltip: {
    fontSize: '0.9em',
  },
  tableActionsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    position: 'relative',
    height: 'auto',
  },
  actions: {
    display: 'flex',
    flexBasis: '100%',
    marginBottom: '1rem',
    [theme.breakpoints.up('md')]: {
      flexBasis: 'unset',
      position: 'absolute',
      right: '0',
    },
    marginLeft: 'auto',
    [theme.breakpoints.down('sm')]: {
      position: 'fixed',
      justifyContent: 'flex-end',
      right: 0,
      bottom: 0,
      left: 0,
      zIndex: 2,
      padding: '0.5rem 1.5rem',
      marginBottom: 0,
      backgroundColor: theme.palette.footerBackground,
    },
  },
  actionButton: {
    flexBasis: 'auto',
    '&:nth-child(1)': {
      [theme.breakpoints.up('md')]: {
        flexGrow: 1,
      },
    },
    '&:nth-child(2)': {
      marginLeft: '1rem',
      [theme.breakpoints.up('md')]: {
        flexGrow: 1,
      },
    },
  },
}));

export default function TableActions() {
  const classes = useStyles();
  const {
    selection,
    deleteDialog,
    transferDialog,
    getChangeStatusTooltipText,
    filter,
    setFilter,
    isDeleteSelectionDisabled,
    updatePage,
    isAddressBookPage,
    changeStatusEntries,
    anchorChangeStatusMenu,
    handleChangeStatusMenuClick,
    handleChangeStatusMenuClose,
    isChangeStatusSelectionDisabled,
    isMoveSelectionDisabled,
    tableConfiguration,
  } = useContext(DraftManagementContext);
  const { t } = useTranslation();
  const { isMediumOrBigger } = useScreenSize();

  const [footerGap, setFooterGap] = useState(0);

  useEffect(() => {
    // after scrolling to the bottom set footer gap to render fixed actions bar above it
    window.addEventListener('scroll', () => {
      const footerHeight = 57;
      if (window.innerHeight + window.scrollY + footerHeight >= document.body.scrollHeight) {
        const gapFromBottom = window.innerHeight + window.scrollY + footerHeight - document.body.scrollHeight;
        setFooterGap(gapFromBottom);
      } else {
        setFooterGap(0);
      }
    });
  }, []);

  const renderEntries = () => {
    let components;
    if (changeStatusEntries?.length) {
      components = changeStatusEntries.map((entry, index) => {
        return (
          <MenuItem
            key={index}
            id={entry.text}
            aria-label={`action-${entry.text}`}
            disabled={entry.disabled}
            onClick={() => entry.onClick()}
          >
            <ListItemIcon>{{ ...entry.icon, props: { ...entry.icon?.props, fontSize: 'small' } }}</ListItemIcon>
            <ListItemText primary={t(entry.text)} />
          </MenuItem>
        );
      });
    }
    return components;
  };

  return (
    <div className={classes.tableActionsContainer}>
      <TableFilter
        isLargeScreen={isMediumOrBigger}
        filter={filter}
        setFilter={setFilter}
        updatePage={updatePage}
        isAddressBookPage={isAddressBookPage}
        columnsConfiguration={tableConfiguration?.columns}
      />

      {selection?.length > 0 && (
        <div
          className={classes.actions}
          style={isMediumOrBigger ? { bottom: 'unset' } : { bottom: footerGap }}
          aria-label={t('messages.actions.selected.submissions')}
          id="multipleSelection"
        >
          <Button
            color="secondary"
            className={classes.actionButton}
            disabled={!isAddressBookPage && isDeleteSelectionDisabled}
            onClick={deleteDialog.open(selection)}
            startIcon={isMediumOrBigger ? <DeleteIcon /> : null}
          >
            {t('actions.delete')}
            {selection?.length < 2 ? '' : ` (${selection?.length})`}
            <div className={classes.visuallyHidden}>{t('Submissions')}</div>
          </Button>
          {!isAddressBookPage && (
            <Tooltip classes={{ tooltip: classes.tooltip }} title={getChangeStatusTooltipText(selection)}>
              <span className={classes.actionButton}>
                <Button
                  color="primary"
                  startIcon={isMediumOrBigger && <AutorenewIcon />}
                  aria-haspopup="true"
                  aria-expanded={anchorChangeStatusMenu ? 'true' : undefined}
                  onClick={handleChangeStatusMenuClick}
                  endIcon={<KeyboardArrowDown />}
                  disabled={isChangeStatusSelectionDisabled}
                >
                  {t('actions.status.change')}
                </Button>
                <Menu
                  id="actions-menu"
                  anchorEl={anchorChangeStatusMenu}
                  open={Boolean(anchorChangeStatusMenu)}
                  onClose={handleChangeStatusMenuClose}
                  getContentAnchorEl={null}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                  }}
                  MenuListProps={{
                    'aria-labelledby': 'basic-button',
                  }}
                >
                  {renderEntries()}
                </Menu>
              </span>
            </Tooltip>
          )}
          <Button
            color="secondary"
            className={classes.actionButton}
            disabled={!isAddressBookPage && isMoveSelectionDisabled}
            onClick={transferDialog.open(selection)}
            startIcon={isMediumOrBigger ? <ImportExport /> : null}
          >
            {t('actions.submission.transfer')}
            {selection?.length < 2 ? '' : ` (${selection?.length})`}
            <div className={classes.visuallyHidden}>{t('Submissions')}</div>
          </Button>
        </div>
      )}
    </div>
  );
}
